import React, { useCallback, useEffect, useState } from "react";
import { TextField, Select, MenuItem, Button, CircularProgress } from "@material-ui/core";
import styled from "styled-components";
import {
	getSystemConfig,
	saveSystemConfig,
	saveBroadcastDraft,
	SystemConfig,
} from "../../../services/SystemConfigService";
import StationPortalBroadcastRichTextEditor from "../../controls/components/stationPortalBroadcast/StationPortalBroadcastRichTextEditor";
import BroadcastPreview from "./BroadcastPreview";
import PermissionGate from "../../controls/auth/PermissionGate";

/** Matches `DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML` in whalelo-backend UtilityController. */
const DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML = "<p>Thông báo từ WhaleLO.</p>";

const Panel = styled.div`
	background: #fff;
	border-radius: 12px;
	padding: 20px 24px;
	margin-bottom: 24px;
	border: 1px solid #e5e7eb;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	max-width: 100%;
`;

const PanelTitle = styled.h2`
	font-size: 18px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 6px 0;
`;

const PanelDesc = styled.p`
	font-size: 13px;
	color: #6b7280;
	margin: 0 0 18px 0;
	line-height: 1.5;
`;

const FieldBlock = styled.div`
	margin-bottom: 16px;
`;

const FieldHelp = styled.div`
	font-size: 12px;
	color: #6b7280;
	margin-top: 4px;
	line-height: 1.4;
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 8px;
	flex-wrap: wrap;
`;

const SystemConfigLink = styled.a`
	font-size: 12px;
	color: #2563eb;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

function toDatetimeLocalValue(iso: string): string {
	if (!iso || typeof iso !== "string") return "";
	const d = new Date(iso.trim());
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
		d.getMinutes()
	)}`;
}

function fromDatetimeLocalValue(local: string): string {
	if (!local || typeof local !== "string") return "";
	const d = new Date(local);
	if (Number.isNaN(d.getTime())) return "";
	return d.toISOString();
}

function defaultEndDateTimeLocal(): string {
	const d = new Date();
	d.setDate(d.getDate() + 7);
	d.setHours(23, 59, 0, 0);
	return toDatetimeLocalValue(d.toISOString());
}

type FormLayout = "page" | "embed";

/**
 * Tạo / cập nhật thông báo toàn trạm trên Station portal (cùng nguồn system-config, public `stationPortalBroadcast`).
 * `embed` = nội dung form bên trong Dialog (không bọc Panel ngoài).
 */
export default function StationPortalBroadcastForm(props: {
	layout?: FormLayout;
	/** Gọi sau khi bấm gửi thành công (ví dụ refresh lịch sử + đóng popup) */
	onSent?: () => void;
	/** Gọi sau khi lưu nháp thành công */
	onDraftSaved?: () => void;
}) {
	const layout = props.layout ?? "page";
	const onSent = props.onSent;
	const onDraftSaved = props.onDraftSaved;
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [draftSaving, setDraftSaving] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [baseConfig, setBaseConfig] = useState<SystemConfig | null>(null);

	const [displayMode, setDisplayMode] = useState<"popup" | "floating_button">("popup");
	const [startsAt, setStartsAt] = useState("");
	const [endsAt, setEndsAt] = useState("");
	const [title, setTitle] = useState("");
	const [bodyHtml, setBodyHtml] = useState(DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const cfg = await getSystemConfig();
			setBaseConfig(cfg);
			setLoadError(null);
			setDisplayMode(cfg.stationPortalBroadcastDisplayMode === "floating_button" ? "floating_button" : "popup");
			setStartsAt(
				cfg.stationPortalBroadcastStartsAt ? toDatetimeLocalValue(cfg.stationPortalBroadcastStartsAt) : ""
			);
			setEndsAt(
				cfg.stationPortalBroadcastEndsAt
					? toDatetimeLocalValue(cfg.stationPortalBroadcastEndsAt)
					: defaultEndDateTimeLocal()
			);
			setTitle(String(cfg.stationPortalBroadcastTitle ?? ""));
			setBodyHtml(String(cfg.stationPortalBroadcastBody ?? "") || DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML);
		} catch {
			setBaseConfig(null);
			setLoadError("Không tải được cấu hình. Kiểm tra mạng hoặc quyền admin.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const onCreateAndSend = async () => {
		if (!baseConfig) {
			void load();
			return;
		}

		const bodyToSave = String(bodyHtml ?? "").trim() || DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML;
		const endIso = endsAt.trim() ? fromDatetimeLocalValue(endsAt.trim()) : "";
		if (!endIso) {
			window.alert("Cần thời điểm kết thúc hợp lệ (ngày giờ kết thúc hiển thị trên portal).");
			return;
		}
		if (Number.isNaN(Date.parse(endIso))) {
			window.alert("Thời điểm kết thúc không hợp lệ.");
			return;
		}

		const startIso = startsAt.trim() ? fromDatetimeLocalValue(startsAt.trim()) : "";
		if (startsAt.trim() && (Number.isNaN(Date.parse(startIso)) || Date.parse(startIso) >= Date.parse(endIso))) {
			window.alert("Thời điểm bắt đầu phải trước thời điểm kết thúc (hoặc để trống để có hiệu lực ngay).");
			return;
		}

		setSaving(true);
		try {
			const next: SystemConfig = {
				...baseConfig,
				stationPortalBroadcastEnabled: true,
				stationPortalBroadcastTitle: String(title ?? "")
					.trim()
					.slice(0, 200),
				stationPortalBroadcastBody: bodyToSave.slice(0, 12000),
				stationPortalBroadcastStartsAt: startIso,
				stationPortalBroadcastEndsAt: endIso,
				stationPortalBroadcastDisplayMode: displayMode,
			};

			await saveSystemConfig(next);
			setBaseConfig(next);
			window.alert(
				"Đã tạo và bật thông báo. Các trạm sẽ thấy nội dung trên Station portal khi tải trang trong khoảng thời gian đã chọn."
			);
			onSent?.();
		} catch (err: any) {
			const msg = err?.response?.data?.message || err?.message || "Lưu thất bại.";
			window.alert(msg);
		} finally {
			setSaving(false);
		}
	};

	const onSaveDraft = async () => {
		const bodyToSave = String(bodyHtml ?? "").trim() || DEFAULT_STATION_PORTAL_BROADCAST_BODY_HTML;
		const endIso = endsAt.trim() ? fromDatetimeLocalValue(endsAt.trim()) : "";
		if (!endIso) {
			window.alert("Cần thời điểm kết thúc hợp lệ để lưu nháp.");
			return;
		}
		const startIso = startsAt.trim() ? fromDatetimeLocalValue(startsAt.trim()) : "";

		setDraftSaving(true);
		try {
			await saveBroadcastDraft({
				title: String(title ?? "")
					.trim()
					.slice(0, 200),
				body: bodyToSave.slice(0, 12000),
				startsAt: startIso,
				endsAt: endIso,
				displayMode,
			});
			window.alert("Đã lưu nháp thành công.");
			onDraftSaved?.();
			onSent?.();
		} catch (err: any) {
			const msg = err?.response?.data?.message || err?.message || "Lưu nháp thất bại.";
			window.alert(msg);
		} finally {
			setDraftSaving(false);
		}
	};

	const formBody = (
		<>
			<FieldBlock
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
					gap: 12,
				}}>
				<TextField
					variant="outlined"
					size="small"
					fullWidth
					label="Bắt đầu (tuỳ chọn)"
					type="datetime-local"
					value={startsAt}
					onChange={(e) => setStartsAt(e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					variant="outlined"
					size="small"
					fullWidth
					label="Kết thúc (bắt buộc)"
					type="datetime-local"
					value={endsAt}
					onChange={(e) => setEndsAt(e.target.value)}
					InputLabelProps={{ shrink: true }}
					required
				/>
			</FieldBlock>

			<FieldHelp style={{ marginTop: -8, marginBottom: 12 }}>
				Để trống bắt đầu = hiển thị ngay sau khi bật (trong phạm vi tới thời điểm kết thúc). Sau khi hết hạn,
				portal không còn hiển thị.
			</FieldHelp>

			<FieldBlock>
				<TextField
					variant="outlined"
					size="small"
					fullWidth
					label="Tiêu đề (tuỳ chọn)"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
				/>
				<FieldHelp>Để trống sẽ dùng mặc định "Thông báo từ WhaleLO".</FieldHelp>
			</FieldBlock>

			<FieldBlock>
				<div style={{ fontSize: 12, color: "#374151", fontWeight: 600, marginBottom: 6 }}>
					Nội dung thông báo
				</div>
				<StationPortalBroadcastRichTextEditor
					value={bodyHtml}
					onChange={setBodyHtml}
					disabled={saving}
				/>
			</FieldBlock>

			<FieldBlock>
				<div style={{ fontSize: 12, color: "#374151", fontWeight: 600, marginBottom: 6 }}>Cách hiển thị</div>
				<Select
					variant="outlined"
					fullWidth
					value={displayMode}
					onChange={(e) => setDisplayMode(e.target.value as "popup" | "floating_button")}>
					<MenuItem value="popup">Popup (tự mở một lần mỗi phiên)</MenuItem>
					<MenuItem value="floating_button">Nút thông báo nổi</MenuItem>
				</Select>
			</FieldBlock>

			<Actions>
				<PermissionGate permission="settings.manage">
					<Button
						variant="outlined"
						color="primary"
						disabled={saving || draftSaving}
						onClick={() => void onSaveDraft()}>
						{draftSaving ? "Đang lưu nháp..." : "Lưu nháp"}
					</Button>
				</PermissionGate>
				<Button
					variant="outlined"
					disabled={saving || draftSaving}
					onClick={() => setPreviewOpen(true)}>
					Xem trước
				</Button>
				{(saving || draftSaving) && <CircularProgress size={20} />}
				<SystemConfigLink
					href="/system-config"
					target="_self">
					Cấu hình nâng cao (tắt, chỉnh job khác) →
				</SystemConfigLink>
			</Actions>

			<BroadcastPreview
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				title={title}
				bodyHtml={bodyHtml}
				displayMode={displayMode}
			/>
		</>
	);

	if (loading) {
		if (layout === "embed") {
			return (
				<div style={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<CircularProgress size={36} />
				</div>
			);
		}

		return (
			<Panel>
				<div style={{ textAlign: "center", padding: 24 }}>
					<CircularProgress size={32} />
				</div>
			</Panel>
		);
	}

	if (loadError || !baseConfig) {
		if (layout === "embed") {
			return (
				<div style={{ padding: "8px 0" }}>
					<p style={{ color: "#b91c1c", fontSize: 14, margin: "0 0 12px" }}>{loadError}</p>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => void load()}>
						Thử tải lại
					</Button>
				</div>
			);
		}

		return (
			<Panel>
				<PanelTitle>Thông báo tới tất cả trạm (Station portal)</PanelTitle>
				<p style={{ color: "#b91c1c", fontSize: 14, margin: "0 0 12px" }}>{loadError}</p>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => void load()}>
					Thử tải lại
				</Button>
			</Panel>
		);
	}

	if (layout === "embed") {
		return <div style={{ paddingTop: 8 }}>{formBody}</div>;
	}

	return (
		<Panel>
			<PanelTitle>Thông báo tới tất cả trạm (Station portal)</PanelTitle>
			<PanelDesc>
				Đặt khung thời gian, tiêu đề, nội dung và cách hiển thị, rồi bấm <strong>Tạo và gửi tới trạm</strong>.
			</PanelDesc>
			{formBody}
		</Panel>
	);
}
